import { gql, useMutation, useQuery } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal/Modal';
import validateEmail from '@orn/shared-web/lib/validation/emailValidation';
import formatTimeAgo from '@orn/shared-web/utils/datetime/formatTimeAgo';
import * as React from 'react';

import Button from '../../components/Button';
import Header from '../../components/Header';
import Input from '../../components/Input';
import Table from '../../components/Table';
import { useToast } from '../../components/Toast';

import type {
  AdminGetRecentAlphaAllowListEntriesQuery,
  AdminSearchAlphaAllowListQuery,
  AdminSearchAlphaAllowListQueryVariables,
  AdminAddToAlphaAllowListMutation,
  AdminAddToAlphaAllowListMutationVariables,
  AdminRemoveFromAlphaAllowListMutation,
  AdminRemoveFromAlphaAllowListMutationVariables,
} from './__generated__/AlphaAllowListScene.gql';

const ADMIN_GET_RECENT_ENTRIES = gql`
  query AdminGetRecentAlphaAllowListEntries {
    adminGetRecentAlphaAllowListEntries {
      id
      normalizedEmail
      addedByUserEmail
      createdAt
    }
  }
`;

const ADMIN_SEARCH_ENTRIES = gql`
  query AdminSearchAlphaAllowList($query: String!) {
    adminSearchAlphaAllowList(query: $query) {
      id
      normalizedEmail
      addedByUserEmail
      createdAt
    }
  }
`;

const ADMIN_ADD_TO_ALLOW_LIST = gql`
  mutation AdminAddToAlphaAllowList($email: String!) {
    adminAddToAlphaAllowList(email: $email) {
      id
      normalizedEmail
      addedByUserEmail
      createdAt
    }
  }
`;

const ADMIN_REMOVE_FROM_ALLOW_LIST = gql`
  mutation AdminRemoveFromAlphaAllowList($normalizedEmail: String!) {
    adminRemoveFromAlphaAllowList(normalizedEmail: $normalizedEmail)
  }
`;

const BYPASS_DOMAINS = ['gomantle.com', 'onrepeatnow.com'];

/**
 * Alpha allow list admin tool. Operators can add, search, and remove emails
 * that bypass the closed-alpha gate on the consumer product. Migrated from the
 * consumer `@orn/web` admin pane — same GraphQL contract, different transport
 * (mops-api) and operator-tuned styling.
 */
const AlphaAllowListScene = () => {
  const [addEmail, setAddEmail] = React.useState('');
  const [addError, setAddError] = React.useState<string | undefined>();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [removeModalOpen, setRemoveModalOpen] = React.useState(false);
  const [emailToRemove, setEmailToRemove] = React.useState<string | null>(null);
  const { showToast } = useToast();

  const {
    data: recentData,
    loading: recentLoading,
    refetch: refetchRecent,
  } = useQuery<AdminGetRecentAlphaAllowListEntriesQuery>(ADMIN_GET_RECENT_ENTRIES);

  const {
    data: searchData,
    loading: searchLoading,
    refetch: refetchSearch,
  } = useQuery<AdminSearchAlphaAllowListQuery, AdminSearchAlphaAllowListQueryVariables>(ADMIN_SEARCH_ENTRIES, {
    variables: { query: searchQuery },
    skip: searchQuery.length === 0,
  });

  const [addToAllowList, { loading: addLoading }] = useMutation<
    AdminAddToAlphaAllowListMutation,
    AdminAddToAlphaAllowListMutationVariables
  >(ADMIN_ADD_TO_ALLOW_LIST);

  const [removeFromAllowList] = useMutation<
    AdminRemoveFromAlphaAllowListMutation,
    AdminRemoveFromAlphaAllowListMutationVariables
  >(ADMIN_REMOVE_FROM_ALLOW_LIST);

  const isAddValid = React.useMemo(() => {
    const validation = validateEmail(addEmail);
    if (!validation.isValid) {
      return false;
    }
    const domain = addEmail.trim().split('@')[1]?.toLowerCase();
    return !BYPASS_DOMAINS.includes(domain ?? '');
  }, [addEmail]);

  const handleAdd = async () => {
    setAddError(undefined);
    const validation = validateEmail(addEmail);
    if (!validation.isValid) {
      setAddError(validation.error);
      return;
    }
    const domain = addEmail.trim().split('@')[1]?.toLowerCase();
    if (BYPASS_DOMAINS.includes(domain ?? '')) {
      setAddError(`@${domain} emails are already allowed.`);
      return;
    }
    const trimmedEmail = addEmail.trim();
    try {
      const result = await addToAllowList({ variables: { email: trimmedEmail } });
      // mops-api's adminAddToAlphaAllowList resolver returns null when the
      // email is already on the list (server-side dedupe), not a GraphQL error.
      if (result.data?.adminAddToAlphaAllowList) {
        showToast({ type: 'success', message: `Added ${trimmedEmail} to the alpha allow list.` });
      } else {
        showToast({ type: 'info', message: `${trimmedEmail} is already on the alpha allow list.` });
      }
      setAddEmail('');
      await refetchRecent();
      if (searchQuery) {
        await refetchSearch();
      }
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
      showToast({ type: 'error', message: 'Failed to add email. Please try again.' });
    }
  };

  const handleRemoveConfirm = async () => {
    if (!emailToRemove) {
      return;
    }
    try {
      await removeFromAllowList({ variables: { normalizedEmail: emailToRemove } });
      showToast({ type: 'success', message: `Removed ${emailToRemove} from the alpha allow list.` });
      setRemoveModalOpen(false);
      setEmailToRemove(null);
      await refetchRecent();
      if (searchQuery) {
        await refetchSearch();
      }
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
      // Leave the modal open with `emailToRemove` populated so the operator can
      // retry the destructive action without re-selecting the row.
      showToast({ type: 'error', message: 'Failed to remove email. Please try again.' });
    }
  };

  const entries = searchQuery
    ? (searchData?.adminSearchAlphaAllowList ?? [])
    : (recentData?.adminGetRecentAlphaAllowListEntries ?? []);
  const isLoading = searchQuery ? searchLoading : recentLoading;

  const heading = searchQuery
    ? `Search · ${entries.length} ${entries.length === 1 ? 'match' : 'matches'}`
    : `Recent · ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`;

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-4 px-6 py-5">
        <h1 className="text-sm font-semibold uppercase tracking-widest">Alpha allow list</h1>

        {/* Controls row: add + search side by side */}
        <div className="border-border-primary flex items-end gap-6 border-b pb-4">
          <div className="flex items-end gap-2">
            <Input
              className="w-72"
              type="email"
              label="Add email"
              placeholder="user@example.com"
              value={addEmail}
              onChange={(value) => {
                setAddEmail(value);
                setAddError(undefined);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isAddValid) {
                  void handleAdd();
                }
              }}
              isInvalid={Boolean(addError)}
              hint={addError}
            />
            <Button
              color="primary"
              size="sm"
              onClick={() => void handleAdd()}
              isLoading={addLoading}
              isDisabled={!isAddValid}
            >
              Add
            </Button>
          </div>
          <Input
            className="w-72"
            type="search"
            label="Search"
            placeholder="Filter by email…"
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </div>

        {/* Entries table */}
        <div className="flex flex-col gap-2">
          <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">{heading}</h2>

          {isLoading ? (
            <span className="text-fg-secondary text-xs">Loading…</span>
          ) : entries.length === 0 ? (
            <span className="text-fg-tertiary text-xs italic">{searchQuery ? 'No matches.' : 'No entries yet.'}</span>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Email</Table.HeaderCell>
                  <Table.HeaderCell>Added</Table.HeaderCell>
                  <Table.HeaderCell>Added by</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {entries.map((entry) => (
                  <Table.Row key={entry.id}>
                    <Table.Cell mono>{entry.normalizedEmail}</Table.Cell>
                    <Table.Cell muted>{formatTimeAgo(entry.createdAt)}</Table.Cell>
                    <Table.Cell mono muted>
                      {entry.addedByUserEmail ?? '—'}
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        color="danger"
                        size="xs"
                        onClick={() => {
                          setEmailToRemove(entry.normalizedEmail);
                          setRemoveModalOpen(true);
                        }}
                      >
                        Remove
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>

        {/* Remove confirmation modal */}
        <Modal isOpen={removeModalOpen} onOpenChange={setRemoveModalOpen}>
          <Modal.Header>Remove from alpha allow list</Modal.Header>
          <Modal.Body>
            <p className="text-fg-primary text-sm">
              Are you sure you want to remove <strong>{emailToRemove}</strong> from the alpha allow list? They will not
              be able to log in until re-added.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setRemoveModalOpen(false)}>
                Cancel
              </Button>
              <Button color="danger" size="sm" onClick={() => void handleRemoveConfirm()}>
                Remove
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </main>
    </div>
  );
};

export default AlphaAllowListScene;
